/** P27 (Feb 26, 2026) — Agency Settings admin page.
 *
 * One place for AZAI admins to manage:
 *   1. Per-agency identity (NPI, Tax ID, Medicaid Provider #, Taxonomy, Address)
 *   2. Global ODM SFTP credentials (read-only display + test)
 *
 * Credentials live in environment variables; this page is read-mostly
 * with "Test Connection" buttons. Agency identity fields are fully
 * editable and persist via PUT /api/agencies/{id}.
 *
 * P32 (Feb 27, 2026) — Refactored from a 625-line single-file component
 * into an orchestrator + tab modules + shared atoms.
 * P49 (Feb 28, 2026) — SOAP tab removed; AZAI is now SFTP-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, IdentificationCard, Plug, ShieldCheck,
} from '@phosphor-icons/react';

import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { logger } from '../lib/logger';

import AgencyIdentityTab from './agency-settings/AgencyIdentityTab';
import OdmSftpTab from './agency-settings/OdmSftpTab';

const API = process.env.REACT_APP_BACKEND_URL || '';

/** Normalize an agency DB doc into the editable form-state shape. */
function buildAgencyForm(a) {
  const contracts = a.contracts || [];
  const medicaid = contracts.find((c) => c.contract_type === 'medicaid');
  const dodd = contracts.find((c) => c.contract_type === 'dodd');
  return {
    id: a.id,
    name: a.name || '',
    npi: a.npi || '',
    tax_id: a.tax_id || '',
    taxonomy_code: a.taxonomy_code || '251E00000X',
    address: {
      street1: a.address?.street1 || a.address?.street || '',
      city: a.address?.city || '',
      state: a.address?.state || 'OH',
      zip_code: a.address?.zip_code || a.address?.zip || '',
    },
    contact: {
      name: a.contact?.name || '',
      phone: a.contact?.phone || '',
      email: a.contact?.email || '',
    },
    medicaid_provider_id: medicaid?.provider_id || '',
    dodd_contract_number: dodd?.contract_number || '',
    _raw_contracts: contracts,
  };
}

/** Merge the editable form-state back into the contracts array for PUT. */
function buildContractsPayload(agencyForm) {
  const baseContracts = (agencyForm._raw_contracts || []).filter(
    (c) => !['medicaid', 'dodd'].includes(c.contract_type)
  );
  const medicaidContract = (agencyForm._raw_contracts || []).find((c) => c.contract_type === 'medicaid') || { contract_type: 'medicaid', enabled: true };
  const doddContract = (agencyForm._raw_contracts || []).find((c) => c.contract_type === 'dodd') || { contract_type: 'dodd', enabled: false };

  return [
    ...baseContracts,
    {
      ...medicaidContract,
      contract_type: 'medicaid',
      enabled: Boolean(agencyForm.medicaid_provider_id),
      provider_id: agencyForm.medicaid_provider_id || null,
    },
    {
      ...doddContract,
      contract_type: 'dodd',
      enabled: Boolean(agencyForm.dodd_contract_number),
      contract_number: agencyForm.dodd_contract_number || null,
      // CODB tier intentionally omitted at the agency level — it is
      // resolved per-client from `patient.county` via codb_resolver
      // (OAC 5123-9-14 Appendix C). Preserve any existing value so
      // we don't break the audit trail on legacy contracts.
      codb_tier: doddContract.codb_tier ?? null,
    },
  ];
}

const AgencySettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAzaiStaff = user?.entity_type === 'azai';

  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [agencyForm, setAgencyForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [creds, setCreds] = useState(null);
  const [credsLoading, setCredsLoading] = useState(true);

  const [sftpTest, setSftpTest] = useState({ running: false, result: null });

  // ── Load agencies on mount ──────────────────────────────────────
  const loadAgencies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/agencies/`, { withCredentials: true });
      const list = res.data?.agencies || [];
      setAgencies(list);
      if (list.length && !selectedAgencyId) setSelectedAgencyId(list[0].id);
    } catch (e) {
      logger.error('Failed to load agencies', e);
      toast.error('Failed to load agencies');
    }
  }, [selectedAgencyId]);

  const loadCredentials = useCallback(async () => {
    setCredsLoading(true);
    try {
      const res = await axios.get(`${API}/api/integrations/odm/credentials`, { withCredentials: true });
      setCreds(res.data);
    } catch (e) {
      logger.error('Failed to load credentials', e);
      toast.error('Failed to load ODM credentials');
    } finally {
      setCredsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAzaiStaff) {
      loadAgencies();
      loadCredentials();
    }
  }, [isAzaiStaff, loadAgencies, loadCredentials]);

  // Sync form with selected agency
  useEffect(() => {
    if (!selectedAgencyId) return;
    const a = agencies.find((x) => x.id === selectedAgencyId);
    if (!a) return;
    setAgencyForm(buildAgencyForm(a));
  }, [selectedAgencyId, agencies]);

  const handleSave = async () => {
    if (!agencyForm) return;
    setSaving(true);
    try {
      const payload = {
        name: agencyForm.name,
        npi: agencyForm.npi,
        tax_id: agencyForm.tax_id,
        taxonomy_code: agencyForm.taxonomy_code,
        address: agencyForm.address,
        contact: agencyForm.contact,
        contracts: buildContractsPayload(agencyForm),
      };
      await axios.put(`${API}/api/agencies/${agencyForm.id}`, payload, { withCredentials: true });
      toast.success('Agency settings saved');
      await loadAgencies();
    } catch (e) {
      logger.error('Save agency failed', e);
      toast.error(e.response?.data?.detail || 'Failed to save agency');
    } finally {
      setSaving(false);
    }
  };

  // ── Connection tests ────────────────────────────────────────────
  const testSftp = async () => {
    setSftpTest({ running: true, result: null });
    try {
      const res = await axios.get(`${API}/api/odm-sftp/test-connection`, { withCredentials: true });
      setSftpTest({ running: false, result: res.data });
      toast[res.data?.success ? 'success' : 'error'](
        res.data?.success ? 'SFTP connection successful' : `SFTP test failed: ${res.data?.error || 'unknown'}`
      );
    } catch (e) {
      setSftpTest({ running: false, result: { success: false, error: e.response?.data?.detail || e.message } });
      toast.error('SFTP test failed');
    }
  };

  // ── Permission gate ────────────────────────────────────────────
  if (!isAzaiStaff) {
    return (
      <div className="p-8 text-center" data-testid="not-azai-gate">
        <ShieldCheck size={48} className="mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">Only AZAI staff can manage agency settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agency-settings-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/agencies')} data-testid="back-to-agencies-btn">
            <ArrowLeft size={16} className="mr-2" /> Back to Agency Management
          </Button>
          <h1 className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope' }} data-testid="page-title">
            Agency Settings
          </h1>
          <p className="text-slate-500 mt-1">
            Per-agency EDI identity + AZAI&apos;s global ODM connectivity, in one place.
          </p>
        </div>
      </div>

      <Tabs defaultValue="identity" className="space-y-6" data-testid="settings-tabs">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="identity" data-testid="tab-identity">
            <IdentificationCard size={16} className="mr-2" />
            Agency Identity
          </TabsTrigger>
          <TabsTrigger value="sftp" data-testid="tab-sftp">
            <Plug size={16} className="mr-2" />
            ODM SFTP (Global)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="identity">
          <AgencyIdentityTab
            agencies={agencies}
            selectedAgencyId={selectedAgencyId}
            setSelectedAgencyId={setSelectedAgencyId}
            agencyForm={agencyForm}
            setAgencyForm={setAgencyForm}
            saving={saving}
            onSave={handleSave}
            onReload={loadAgencies}
          />
        </TabsContent>

        <TabsContent value="sftp">
          <OdmSftpTab
            creds={creds}
            credsLoading={credsLoading}
            sftpTest={sftpTest}
            onTest={testSftp}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AgencySettingsPage;
